import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { HELP_ARTICLES, ARTICLE_CATEGORIES } from '../../../data/helpArticles';

const KnowledgeBaseSearch = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    ...ARTICLE_CATEGORIES.map(c => ({ value: String(c.id), label: c.name })),
  ];

  const filteredArticles = HELP_ARTICLES.filter((article) => {
    const matchesSearch = searchQuery === '' ||
      article?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      article?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      article?.tags?.some(t => t.includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || String(article?.categoryId) === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Icon
              name="Search"
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
            <Input
              type="search"
              placeholder="Search knowledge base articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div className="w-full md:w-64">
          <Select
            options={categoryOptions}
            value={selectedCategory}
            onChange={setSelectedCategory}
            placeholder="Filter by category"
          />
        </div>
      </div>

      {/* Popular Categories */}
      <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-[#0f1410] border-[#1a2820]' : 'bg-gray-50 border-gray-200'}`}>
        <h2 className="text-lg font-semibold text-foreground mb-4">Popular Categories</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {ARTICLE_CATEGORIES.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(selectedCategory === String(category.id) ? 'all' : String(category.id))}
              className={`p-4 rounded-lg border text-left transition-all ${
                selectedCategory === String(category.id)
                  ? isDarkMode
                    ? 'bg-[#10b981]/10 border-[#10b981] text-[#10b981]'
                    : 'bg-[#107570]/10 border-[#107570] text-[#107570]'
                  : isDarkMode
                    ? 'bg-[#0a0f0d] border-[#1a2820] hover:border-[#10b981]/50'
                    : 'bg-white border-gray-200 hover:border-[#107570]/50'
              }`}
            >
              <div className="text-sm font-medium">{category.name}</div>
              <div className="text-xs text-muted-foreground mt-1">{category.articles.length} articles</div>
            </button>
          ))}
        </div>
      </div>

      {/* Articles Grid */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            {filteredArticles.length} Article{filteredArticles.length !== 1 ? 's' : ''} Found
          </h2>
        </div>

        {filteredArticles.length === 0 ? (
          <div className={`p-12 rounded-lg border text-center ${isDarkMode ? 'bg-[#0f1410] border-[#1a2820]' : 'bg-gray-50 border-gray-200'}`}>
            <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No articles found</h3>
            <p className="text-sm text-muted-foreground">Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredArticles.map((article) => (
              <button
                key={article.id}
                onClick={() => navigate(`/knowledge-base-articles?article=${article.id}`)}
                className={`p-6 rounded-lg border transition-all text-left w-full ${
                  isDarkMode
                    ? 'bg-[#0f1410] border-[#1a2820] hover:border-[#10b981]/50'
                    : 'bg-white border-gray-200 hover:border-[#107570]/50 hover:shadow-md'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-[#10b981]/10 text-[#10b981]' : 'bg-[#107570]/10 text-[#107570]'}`}>
                        {article.category}
                      </span>
                      <span className="text-xs text-muted-foreground">{article.difficulty}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">{article.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{article.description}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Icon name="Eye" size={14} />
                        {article.views?.toLocaleString()} views
                      </div>
                      <div className="flex items-center gap-1">
                        <Icon name="Clock" size={14} />
                        {article.readTime}
                      </div>
                      <div className="flex items-center gap-1">
                        <Icon name="Calendar" size={14} />
                        Updated {article.lastUpdated}
                      </div>
                    </div>
                  </div>
                  <Icon name="ChevronRight" size={20} className="text-muted-foreground flex-shrink-0 mt-1" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBaseSearch;
